"use client";

import { Route } from "next";
import Link from "next/link";
import Image from "next/image";
import { motion } from "framer-motion";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import classNames from "classnames";
import { Moon, Sun } from "lucide-react";
import { useTheme } from "next-themes";

const links: {
  title: string;
  href: Route;
}[] = [
  {
    title: "O nas",
    href: "/about",
  },
  {
    title: "Oferta",
    href: "/offer",
  },
  {
    title: "Kontakt",
    href: "/contact",
  },
];

const Navigation = () => {
  const pathname = usePathname();
  const [show, setShow] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [mounted, setMounted] = useState(false);
  const { theme, setTheme } = useTheme();

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      setShow(currentScrollY < lastScrollY || currentScrollY <= 200);
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  return (
    <motion.nav
      className={classNames([
        "fixed top-0 z-50 w-full px-4",
        !show && "pointer-events-none",
      ])}
      animate={{
        opacity: 1,
        visibility: "visible",
        transition: {
          delay: pathname === "/" ? 1.4 : 1,
          duration: 1,
        },
      }}
      initial={{ opacity: 0, visibility: "hidden" }}
    >
      <div
        className={classNames([
          "mx-auto flex w-full max-w-[1440px] items-center justify-center gap-4 py-6 transition-all duration-500 lg:grid lg:grid-cols-[250px_1fr_200px]",
          !show && "invisible -translate-y-4 opacity-0",
        ])}
      >
        {mounted && (
          <Link
            href="/"
            className="mix-blend-difference transition-opacity duration-700 hover:opacity-70"
          >
            <Image
              src="/logo-full.svg"
              height={100}
              width={298}
              alt=""
              className="dark-only h-14 w-auto"
            />
            <Image
              src="/logo-full-light.svg"
              height={100}
              width={298}
              alt=""
              className="light-only h-14 w-auto"
            />
          </Link>
        )}
        <div className="hidden grow justify-center sm:flex">
          <ul className="bg-primary text-secondary-main flex items-center rounded-full p-2 transition-colors duration-700 lg:gap-4 lg:text-lg xl:text-xl">
            {links.map(({ title, href }) => (
              <li key={title}>
                <Link href={href} className="group button-base">
                  <div className="button-decor" />
                  {title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        {!!mounted && (
          <div className="text-secondary-main grow">
            <button
              onClick={toggleTheme}
              className="bg-primary group relative isolate ml-auto hidden h-14 w-14 cursor-pointer place-content-center rounded-full sm:grid"
            >
              <div className="button-decor top-1/2 left-1/2 h-10 w-10 -translate-x-1/2 -translate-y-1/2" />
              {theme === "dark" ? <Moon /> : <Sun />}
            </button>
          </div>
        )}
      </div>
    </motion.nav>
  );
};

export default Navigation;
